import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { SORT_DIRECTION, SORT_FIELD, MY_PLAYLIST_ALLOWED_SORT_FIELD } from '../utils/const'
import { getPlaylistDetailActivities } from '../api/playlist'
import {
  CREATE_PLAYLIST,
  DUPLICATE_PLAYLIST,
  UPDATE_PLAYLIST,
  REMOVE_PLAYLIST,
  ACTIVATE_PLAYLIST,
  DEACTIVATE_PLAYLIST,
} from '~/graphql/mutations/playlists'
import {
  GET_PLAYLISTS_LIST,
  GET_PLAYLIST_INFO,
  GET_MY_ACADEMY_PLAYLISTS,
  GET_MY_PLAYLIST_ASSIGNERS,
} from '~/graphql/queries/academyPlaylists'

const mutationTypes = {
  SET_PLAYLIST: 'SET_PLAYLIST',
  SET_PLAYLIST_LOADING: 'SET_PLAYLIST_LOADING',
  SET_PLAYLIST_FILTER: 'SET_PLAYLIST_FILTER',
  SET_PLAYLIST_SORT: 'SET_PLAYLIST_SORT',
  SET_PLAYLISTS_MANAGE: 'SET_PLAYLISTS_MANAGE',
  SET_PLAYLISTS_MANAGE_FILTER: 'SET_PLAYLISTS_MANAGE_FILTER',
  UPDATE_PLAYLIST_INFO: 'UPDATE_PLAYLIST_INFO',
  CLEAR_PLAYLIST_FILTER: 'CLEAR_PLAYLIST_FILTER',
  SET_ASSIGNED_BY_OPTIONS: 'SET_ASSIGNED_BY_OPTIONS',
  SET_CREATING: 'SET_CREATING',
  SET_UPDATING: 'SET_UPDATING',
  ADD_PLAYLIST_AFTER_DUPLICATE: 'ADD_PLAYLIST_AFTER_DUPLICATE',
  REMOVE_PLAYLIST_FROM_LIST: 'REMOVE_PLAYLIST_FROM_LIST',
  SET_PLAYLIST_DETAIL_ACTIVITIES: 'SET_PLAYLIST_DETAIL_ACTIVITIES',
}

const actionTypes = {
  GET_MY_ACADEMY_PLAYLISTS: 'GET_MY_ACADEMY_PLAYLISTS',
  GET_MY_PLAYLIST_DETAIL: 'GET_MY_PLAYLIST_DETAIL',
  SORT_PLAYLIST: 'SORT_PLAYLIST',
  CREATE_PLAYLIST: 'CREATE_PLAYLIST',
  GET_PLAYLIST_INFO: 'GET_PLAYLIST_INFO',
  DUPLICATE_PLAYLIST: 'DUPLICATE_PLAYLIST',
  UPDATE_PLAYLIST: 'UPDATE_PLAYLIST',
  REMOVE_PLAYLIST: 'REMOVE_PLAYLIST',
  GET_PLAYLISTS_LIST: 'GET_PLAYLISTS_LIST',
  ACTIVATE_PLAYLIST: 'ACTIVATE_PLAYLIST',
  DEACTIVATE_PLAYLIST: 'DEACTIVATE_PLAYLIST',
  GET_PLAYLIST_DETAIL_ACTIVITIES: 'GET_PLAYLIST_DETAIL_ACTIVITIES',
  GET_MY_PLAYLIST_ASSIGNED_OPTIONS: 'GET_MY_PLAYLIST_ASSIGNED_OPTIONS',
  GET_PLAYLIST_DETAIL_ACTIVITIES_NEXT_PAGE: 'GET_PLAYLIST_DETAIL_ACTIVITIES_NEXT_PAGE',
}

export const state = () => ({
  playlist: {
    data: [],
    meta: {
      limit: 10,
      page: 1,
      total: 0,
    },
  },
  getting: false,
  loading: false,
  creating: false,
  updating: false,
  filter: {
    assignedBys: null,
    search: '',
    statuses: null,
    isOpen: false,
    assignedOptions: [],
    schoolIds: null,
  },
  sort: {
    sortField: MY_PLAYLIST_ALLOWED_SORT_FIELD.DATE,
    sortDirection: SORT_DIRECTION.DESC,
  },
  error: {
    message: '',
  },
  playlistsManage: {
    data: [],
    meta: {
      limit: 10,
      page: 1,
      total: 0,
    },
    filter: {
      search: null,
      limit: 10,
      page: 1,
      schoolIds: [],
      sortDirection: SORT_DIRECTION.DESC,
      sortField: SORT_FIELD.DATE,
      statuses: null,
    },
  },
  playlistDetailActivities: {
    data: [],
    meta: {
      total: 0,
      page: 1,
    },
    loading: false,
  },
})

export const mutations = {
  [mutationTypes.SET_PLAYLIST](state, payload) {
    state.playlist = payload.playlist
  },
  [mutationTypes.SET_PLAYLIST_FILTER](state, payload) {
    state.filter = {
      ...state.filter,
      ...payload,
    }
  },
  [mutationTypes.SET_PLAYLIST_LOADING](state, payload) {
    state.loading = payload
  },
  [mutationTypes.SET_PLAYLIST_SORT](state, payload) {
    state.sort = payload
  },
  [mutationTypes.CLEAR_PLAYLIST_FILTER](state) {
    state.filter = {
      ...state.filter,
      assignedBys: null,
      statuses: null,
    }
  },
  [mutationTypes.SET_ASSIGNED_BY_OPTIONS](state, payload) {
    state.filter.assignedOptions = payload
  },
  [mutationTypes.SET_CREATING](state, payload) {
    state.creating = payload
  },
  [mutationTypes.SET_UPDATING](state, payload) {
    state.updating = payload
  },
  [mutationTypes.SET_PLAYLISTS_MANAGE](state, payload) {
    state.playlistsManage.data = payload.data
    state.playlistsManage.meta = payload.meta
  },
  [mutationTypes.SET_PLAYLISTS_MANAGE_FILTER](state, payload) {
    state.playlistsManage.filter = { ...state.playlistsManage.filter, ...payload }
  },
  [mutationTypes.UPDATE_PLAYLIST_INFO](state, payload) {
    const { field, value, id } = payload
    const foundIndex = state.playlistsManage.data.findIndex((playlist) => playlist.id === id)

    if (foundIndex > -1) {
      state.playlistsManage.data[foundIndex][field] = value
      state.playlistsManage.data[foundIndex].updatedAt = moment.utc().format('YYYY-MM-DDTHH:mm:ss')
    }
  },
  [mutationTypes.ADD_PLAYLIST_AFTER_DUPLICATE](state, payload) {
    if (payload) {
      state.playlistsManage.data.unshift(payload)
      // state.playlistsManage.meta.total += 1
    }
  },
  [mutationTypes.REMOVE_PLAYLIST_FROM_LIST](state, payload) {
    const foundIndex = state.playlistsManage.data.findIndex((playlist) => playlist.id === payload.id)

    if (foundIndex > -1) {
      state.playlistsManage.data.splice(foundIndex, 1)
    }
  },
  [mutationTypes.SET_PLAYLIST_DETAIL_ACTIVITIES](state, payload) {
    state.playlistDetailActivities = payload
  },
}

export const actions = {
  async [actionTypes.GET_MY_ACADEMY_PLAYLISTS]({ commit, getters }, params) {
    try {
      commit(mutationTypes.SET_PLAYLIST_LOADING, true)
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        query: GET_MY_ACADEMY_PLAYLISTS,
        variables: {
          ...params,
        },
        fetchPolicy: 'no-cache',
      })
      if (params.isLoadMore) {
        commit(mutationTypes.SET_PLAYLIST, {
          playlist: {
            data: [...getters.getPlaylist.data, ...data.academyMyPlaylists.data],
            meta: data.academyMyPlaylists.meta,
          },
        })
      } else {
        commit(mutationTypes.SET_PLAYLIST, { playlist: data.academyMyPlaylists })
      }
      return {
        data: data.academyMyPlaylists,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    } finally {
      commit(mutationTypes.SET_PLAYLIST_LOADING, false)
    }
  },
  async [actionTypes.CREATE_PLAYLIST]({ commit }, params) {
    commit(mutationTypes.SET_CREATING, true)
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: CREATE_PLAYLIST,
        variables: params,
      })
      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    } finally {
      commit(mutationTypes.SET_CREATING, false)
    }
  },
  async [actionTypes.GET_PLAYLIST_INFO]({ commit }, slug) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        fetchPolicy: 'no-cache',
        query: GET_PLAYLIST_INFO,
        variables: {
          slug,
        },
      })
      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.DUPLICATE_PLAYLIST](_, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: DUPLICATE_PLAYLIST,
        variables: {
          playlistId: params.id,
        },
      })

      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.ACTIVATE_PLAYLIST]({ commit }, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: ACTIVATE_PLAYLIST,
        variables: {
          playlistId: params.id,
        },
      })

      if (data && params.updatePlaylists) {
        commit(mutationTypes.UPDATE_PLAYLIST_INFO, params)
      }

      return {
        data,
        success: true,
      }
    } catch (error) {
      console.error('ACTIVATE_PLAYLIST', error)
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.DEACTIVATE_PLAYLIST]({ commit }, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: DEACTIVATE_PLAYLIST,
        variables: {
          playlistId: params.id,
        },
      })

      if (data && params.updatePlaylists) {
        commit(mutationTypes.UPDATE_PLAYLIST_INFO, params)
      }

      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.REMOVE_PLAYLIST]({ commit }, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: REMOVE_PLAYLIST,
        variables: {
          playlistId: params.id,
        },
      })

      if (data && params.updatePlaylists) {
        commit(mutationTypes.REMOVE_PLAYLIST_FROM_LIST, params)
      }

      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.UPDATE_PLAYLIST]({ commit }, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_PLAYLIST,
        variables: {
          ...params,
        },
      })
      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.GET_PLAYLIST_DETAIL_ACTIVITIES]({ commit }, { schoolId, targetId, page, limit }) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const data = await getPlaylistDetailActivities(apolloClient, { schoolId, targetId, page, limit })
      if (data && data.userActivityHistories) {
        commit(mutationTypes.SET_PLAYLIST_DETAIL_ACTIVITIES, data.userActivityHistories)
      }
    } catch (error) {
      console.error(`store/${actionTypes.GET_PLAYLIST_DETAIL_ACTIVITIES}`, error)
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.GET_PLAYLIST_DETAIL_ACTIVITIES_NEXT_PAGE](
    { commit, getters },
    { schoolId, targetId, page, limit }
  ) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const data = await getPlaylistDetailActivities(apolloClient, { schoolId, targetId, page, limit })
      if (data && data.userActivityHistories) {
        commit(mutationTypes.SET_PLAYLIST_DETAIL_ACTIVITIES, {
          ...data.userActivityHistories,
          data: [...getters.getPlaylistDetailActivities.data, ...data.userActivityHistories.data],
        })
      }
    } catch (error) {
      console.error(`store/${actionTypes.GET_PLAYLIST_DETAIL_ACTIVITIES_NEXT_PAGE}`, error)
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.GET_PLAYLISTS_LIST]({ commit, getters }, { filter, isReset = false }) {
    try {
      if (!isReset && isEqual(getters.getPlaylistManageFilter, filter)) {
        return {
          data: getters.getPlaylistManage,
          success: true,
        }
      }

      commit(mutationTypes.SET_PLAYLISTS_MANAGE_FILTER, filter)
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        query: GET_PLAYLISTS_LIST,
        variables: filter,
        fetchPolicy: 'no-cache',
      })
      if (data && data.academyPlaylists) {
        commit(
          mutationTypes.SET_PLAYLISTS_MANAGE,
          isReset
            ? data.academyPlaylists
            : {
                ...data.academyPlaylists,
                data: [...get(getters, 'getPlaylistManage.data', []), ...data.academyPlaylists.data],
              }
        )
      }
      return data
    } catch (error) {
      console.error('GET_PLAYLISTS_LIST', error)
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.GET_MY_PLAYLIST_ASSIGNED_OPTIONS]({ commit, rootGetters }, params) {
    try {
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        query: GET_MY_PLAYLIST_ASSIGNERS,
        variables: {
          ...params,
        },
        fetchPolicy: 'no-cache',
      })
      const assigners = data.academyMyPlaylistAssigners
        .map((assigner) => {
          const personalProfiles = assigner.personalProfiles
          const foundProfile = personalProfiles.find((profile) =>
            [rootGetters['school/schoolProfile'].parentId, rootGetters['school/schoolId']].includes(profile.school.id)
          )
          if (foundProfile) {
            return {
              label: foundProfile.name,
              value: assigner.id,
              avatar: foundProfile.media?.url || '',
            }
          }
          return {
            label: assigner.fullName,
            value: assigner.id,
            avatar: assigner.media?.url || '',
          }
        })
        .filter((item) => item)
      commit(mutationTypes.SET_ASSIGNED_BY_OPTIONS, assigners)
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
}

export const getters = {
  getPlaylist: (state) => state.playlist,
  getPlaylistFilter: (state) => state.filter,
  getPlaylistSort: (state) => state.sort,
  getPlaylistError: (state) => state.error,
  getPlaylistLoading: (state) => state.loading,
  getPlaylistManage: (state) => state.playlistsManage,
  getPlaylistManageFilter: (state) => state.playlistsManage.filter,
  getPlaylistDetailActivities: (state) => state.playlistDetailActivities,
}
